import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Plus, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useQuoteCart } from "@/context/QuoteCartContext";
import { toast } from "sonner";
import SEO from "@/components/SEO";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [heroImage, setHeroImage] = useState("");
  const [notFound, setNotFound] = useState(false);
  const { addItem } = useQuoteCart();

  useEffect(() => {
    api.get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        setSize(r.data.sizes?.[0]?.label || "");
        setHeroImage(r.data.image_url);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <h1 className="font-display text-4xl text-brand-ink">Product not found</h1>
      <Link to="/catalog" className="inline-block mt-6 text-brand-red font-semibold">← Back to catalog</Link>
    </div>
  );

  if (!product) return (
    <div className="max-w-7xl mx-auto px-6 py-24">
      <div className="grid md:grid-cols-2 gap-10">
        <div className="aspect-[4/5] bg-brand-pink/30 rounded-2xl animate-pulse" />
        <div className="space-y-4">
          <div className="h-6 w-24 bg-brand-pink/30 rounded animate-pulse" />
          <div className="h-12 w-3/4 bg-brand-pink/30 rounded animate-pulse" />
          <div className="h-4 w-full bg-brand-pink/30 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );

  const handleAdd = () => {
    addItem({
      product_id: product.id,
      product_name: product.name,
      size_label: size,
      quantity: qty,
      image_url: product.image_url,
    });
    toast.success(`Added ${qty}× ${product.name} (${size}) to quote cart`);
  };

  const gallery = product.gallery?.length ? product.gallery : [product.image_url];

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <SEO
        title={product.name}
        description={product.short_description || product.description?.slice(0, 160)}
        image={product.image_url}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          image: [product.image_url, ...(product.gallery || [])],
          description: product.short_description || product.description,
          category: product.category,
          brand: { "@type": "Brand", name: "Cake World USA" },
          offers: {
            "@type": "AggregateOffer",
            offerCount: (product.sizes || []).length || 2,
            availability: "https://schema.org/InStock",
            priceCurrency: "USD",
            priceSpecification: { "@type": "PriceSpecification", price: "0" },
          },
        }}
      />
      <Link to="/catalog" className="inline-flex items-center gap-2 text-sm text-brand-stone hover:text-brand-ink mb-8" data-testid="back-to-catalog">
        <ArrowLeft className="w-4 h-4" /> Back to catalog
      </Link>

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Gallery */}
        <div>
          <div className="rounded-3xl overflow-hidden bg-brand-pinkLight border border-brand-line shadow-soft aspect-square">
            <img src={heroImage} alt={product.name} className="w-full h-full object-contain p-10" />
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 grid grid-cols-4 gap-3">
              {gallery.map((g, i) => (
                <button
                  key={g}
                  onClick={() => setHeroImage(g)}
                  data-testid={`gallery-thumb-${i}`}
                  className={`aspect-square rounded-xl overflow-hidden border-2 transition ${heroImage === g ? "border-brand-ink" : "border-transparent"}`}
                >
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="eyebrow">{product.category}</div>
          <h1 className="mt-2 font-display text-5xl text-brand-ink leading-none">{product.name}</h1>
          <p className="mt-6 text-brand-stone leading-relaxed">{product.description}</p>

          {/* Size */}
          <div className="mt-8">
            <div className="text-sm font-semibold text-brand-ink mb-3">Choose size</div>
            <div className="flex flex-wrap gap-3" data-testid="size-selector">
              {product.sizes?.map((s) => (
                <button
                  key={s.label}
                  onClick={() => setSize(s.label)}
                  data-testid={`select-size-${s.label}`}
                  className={`px-5 py-3 rounded-2xl border text-left transition ${
                    size === s.label
                      ? "bg-brand-ink text-white border-brand-ink"
                      : "bg-white text-brand-ink border-brand-line hover:border-brand-ink"
                  }`}
                >
                  <div className="font-display text-2xl">{s.label}</div>
                  {s.servings && <div className={`text-xs mt-0.5 ${size === s.label ? "text-white/70" : "text-brand-stone"}`}>{s.servings}</div>}
                </button>
              ))}
            </div>
          </div>

          {/* Qty */}
          <div className="mt-6 flex items-center gap-6">
            <div className="text-sm font-semibold text-brand-ink">Quantity</div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="qty-dec"
                className="w-10 h-10 rounded-full border border-brand-line grid place-items-center hover:bg-brand-pink/30"
              >−</button>
              <span className="w-8 text-center font-semibold text-brand-ink" data-testid="qty-value">{qty}</span>
              <button
                onClick={() => setQty((q) => q + 1)}
                data-testid="qty-inc"
                className="w-10 h-10 rounded-full border border-brand-line grid place-items-center hover:bg-brand-pink/30"
              >+</button>
            </div>
          </div>

          <button
            onClick={handleAdd}
            data-testid="detail-add-to-quote"
            className="mt-8 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-brand-ink text-white text-base font-semibold hover:bg-brand-ink active:scale-95 transition"
          >
            <Plus className="w-5 h-5" /> Add to Quote Cart
          </button>

          {/* Info accordion */}
          <div className="mt-12 space-y-6">
            {product.ingredients && (
              <div>
                <div className="eyebrow">Ingredients</div>
                <p className="mt-2 text-sm text-brand-stone leading-relaxed">{product.ingredients}</p>
              </div>
            )}
            {product.allergens && (
              <div>
                <div className="eyebrow">Allergens</div>
                <p className="mt-2 text-sm text-brand-stone leading-relaxed">{product.allergens}</p>
              </div>
            )}
            <div className="flex flex-wrap gap-4 pt-4 border-t border-brand-line">
              {[
                "Wholesale-only pricing",
                "Cold-chain shipping",
                "Consistent at scale",
              ].map((f) => (
                <div key={f} className="flex items-center gap-2 text-xs text-brand-stone">
                  <Check className="w-4 h-4 text-brand-red" /> {f}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
